
/**
 * UNIVAPLAT-3514: query (read) models for the admin contracts endpoints
 * (`/api/v2/contracts`, backend contract UNIVAPLAT-3498).
 *
 * Contract admin names, emails and change reasons are personal data: never log them
 * (console, Sentry breadcrumbs) and never store them in localStorage.
 */

export interface ContractOrganisationLink {
    id: number,
    organisation: {
        id: number,
        code: string,
        name: string,
    },
}

/** `GET /api/v2/contracts` item and `GET /api/v2/contracts/{id}`. Dates are DATE_ATOM strings. */
export interface Contract {
    id: number,
    title: string,
    code: string,
    serviceProvider: string | null,
    signatureDate: string | null,
    kickoffDate: string | null,
    contractOrganisations: ContractOrganisationLink[],
    /** ISO string when deactivated, otherwise null. */
    disabledAt: string | null,
    /** ISO string when deleted, otherwise null. */
    deletedAt: string | null,
}

/** `GET /api/v2/contracts/{id}/admins` item. `id` is the contract-admin link id (used to remove it), not the user id. */
export interface ContractAdminListItem {
    id: number,
    firstName: string | null,
    lastName: string,
    email: string | null,
    addedOn: string,
}

/** `GET /api/v2/contracts/{id}/logs` item. */
export interface ContractChange {
    id: number,
    change: 'deactivate' | 'reactivate',
    reason: string | null,
    createdAt: string,
}

export interface ContractListFilters {
    title?: string,
    code?: string,
    serviceProvider?: string,
    showDeactivated?: boolean,
    showDeleted?: boolean,
}

export type AdminUserLookupRole = 'admin-contract' | 'admin-org' | 'admin-service' | 'admin-department';

/** `GET /api/v2/users/admins` item, offered by the "link contract admins" popup. */
export interface AdminUserLookupItem {
    id: number,
    firstName: string | null,
    lastName: string,
    email: string | null,
    role: AdminUserLookupRole,
}

export function isContractDeleted(contract: Pick<Contract, 'deletedAt'>): boolean {
    return !!contract.deletedAt;
}

export function isContractDeactivated(contract: Pick<Contract, 'disabledAt' | 'deletedAt'>): boolean {
    return !!contract.disabledAt;
}

export function isContractActive(contract: Pick<Contract, 'disabledAt' | 'deletedAt'>): boolean {
    return !contract.disabledAt && !contract.deletedAt;
}

/**
 * Single mapping point for contract dates: a DATE_ATOM response value to the
 * YYYY-MM-DD string used by date inputs and sent back to the API. Null/empty/invalid gives ''.
 */
export function contractDateToInput(value: string | null | undefined): string {
    if (!value) {
        return '';
    }
    return contractDatePart(value) ?? '';
}

/**
 * The leading YYYY-MM-DD of a date-only API value ('2026-03-05T00:00:00+00:00'), with no timezone
 * conversion, so a browser west of UTC does not show the previous day. Null when absent or invalid.
 */
export function contractDatePart(value: string | null | undefined): string | null {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? '');
    if (!match) {
        return null;
    }
    const [, y, m, d] = match.map(Number);
    const check = new Date(Date.UTC(y, m - 1, d));
    const valid = check.getUTCFullYear() === y && check.getUTCMonth() === m - 1 && check.getUTCDate() === d;
    return valid ? match[0] : null;
}
